"use client";

import React from "react";
import { Building2, ShieldCheck, CircleCheck as CheckCircle2, ArrowRight, Layers, Users, CalendarClock, HardHat, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const audiences = ["Commercial developers", "Main contractors", "Facilities managers", "Residential property owners"];
const useCases = [{
  icon: Building2,
  title: "Façade refurbishment",
  description: "Full-height perimeter scaffolding with protective netting, debris sheeting, and hoist access for external repairs, brickwork restoration, and window replacement."
}, {
  icon: Layers,
  title: "Internal reconfiguration",
  description: "Tailored birdcage scaffolds, crash decks, and temporary support systems engineered for structural alterations, ceilings, and internal refits."
}, {
  icon: CalendarClock,
  title: "Phased renovation programmes",
  description: "Flexible, stage-by-stage erection and dismantling schedules designed to keep trades working efficiently without blocking operational parts of your site."
}];
const accreditations = [{
  name: "NASC Member",
  detail: "National Access & Scaffolding Confederation"
}, {
  name: "CHAS Accredited",
  detail: "Contractors Health and Safety Assessment Scheme"
}];
function RefurbishmentHeroPhoto() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="w-full h-full bg-muted animate-pulse" />;
  }
  const matchedPhoto = posts.find(p => p.title?.toLowerCase().trim() === "refurbishment scaffolding hero image" || p.customFields?.photo_name?.toLowerCase().trim() === "refurbishment scaffolding hero image");
  const imgSrc = matchedPhoto?.customFields?.image || matchedPhoto?.featuredImage;
  const altText = matchedPhoto?.customFields?.alt_text || "Commercial refurbishment scaffolding installed on a multi-storey brick building in the South East";
  if (imgSrc) {
    const image = {
      src: imgSrc,
      srcset: "",
      sizes: ""
    };
    return <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={altText} className="w-full h-full object-cover" loading="lazy" decoding="async" />;
  }
  return <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/refurbishment_scaffolding_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/refurbishment_scaffolding_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/refurbishment_scaffolding_hero.png-wvc-sizes" alt="Commercial refurbishment scaffolding installed on a multi-storey brick building in the South East" className="w-full h-full object-cover" loading="lazy" decoding="async" />;
}
export default function Refurbishment() {
  const shouldReduceMotion = useReducedMotion();
  const heroPhotoQuery = new WP_Query({
    post_type: "site_photo",
    per_page: 50
  });
  return <section id="refurbishmentscaffoldinghero" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Top Breadcrumb / Eyebrow */}
        <div className="flex flex-wrap items-center gap-2 mb-6 text-sm text-muted-foreground">
          <Link to="/HomePage" className="hover:text-primary transition-colors">Home</Link>
          <span>/</span>
          <Link to="/CommercialScaffoldingPage" className="hover:text-primary transition-colors">Services</Link>
          <span>/</span>
          <span className="text-foreground font-medium">Refurbishment Scaffolding</span>
        </div>

        {/* Hero Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Main Content Column */}
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs font-semibold uppercase tracking-wider">
              <HardHat className="size-3.5 text-primary" />
              <span>South East & Home Counties</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
              Refurbishment Scaffolding
            </h1>

            <p className="text-lg text-muted-foreground leading-relaxed">
              Planned, site-specific access solutions for commercial renovations, residential refurbishments, and heritage restorations across Berkshire, Surrey, Hampshire, Buckinghamshire, London, and the wider South East.
            </p>

            {/* Answer-first Value Summary */}
            <div className="p-5 rounded-xl bg-card border border-border shadow-xs space-y-3">
              <div className="flex items-center gap-2 text-foreground font-semibold text-base">
                <Sparkles className="size-4 text-primary" />
                <span>What BMF provides on every refurbishment project</span>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Site-specific 3D design & load planning</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Safety-led erection & weekly inspections</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Strict coordination with main contractors</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Minimal disruption to live building occupants</span>
                </li>
              </ul>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Button asChild size="lg" className="shadow-sm">
                <Link to="/ContactRequestQuotePage">
                  <span>Request a site survey</span>
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/ProjectsCaseStudiesPage">
                  <span>View past projects</span>
                </Link>
              </Button>
            </div>

            {/* Key Audiences list */}
            <div className="pt-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                <Users className="size-3.5" />
                <span>Trusted by project teams</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {audiences.map((aud, i) => <Badge key={i} data-index={i} variant="outline" className="bg-card text-foreground font-normal text-xs py-1 px-3 border-border">
                    {aud}
                  </Badge>)}
              </div>
            </div>
          </div>

          {/* Right Column / Visual Overview Card */}
          <div className="lg:col-span-5 space-y-6">
            <Card className="border-border shadow-md bg-card overflow-hidden">
              <div className="relative aspect-16/10 w-full bg-muted overflow-hidden">
                <PostsProvider wp_query={{
                post_type: "site_photo",
                per_page: 50,
                status: ["publish"]
              }} className="w-full h-full">
                  <RefurbishmentHeroPhoto />
                </PostsProvider>
                <div className="absolute top-3 right-3 pointer-events-none">
                  <Badge className="bg-secondary text-secondary-foreground shadow-xs font-medium">
                    Fully Insured & NASC Certified
                  </Badge>
                </div>
              </div>

              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-bold text-foreground">
                  Safety & Industry Compliance
                </CardTitle>
                <CardDescription className="text-sm">
                  Backed by over 20 years of hands-on scaffolding experience and proven accreditations.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  BMF Scaffolding Ltd operates with the operational rigour of a major contractor while retaining the accountability of a family-run business. Every structure is handed over with complete handover certificates and monitored through routine compliance checks.
                </p>

                <Separator />

                <div className="space-y-2.5">
                  {accreditations.map((item, i) => <div key={i} data-index={i} className="flex items-center justify-between p-2.5 rounded-lg bg-accent/50 border border-border text-sm">
                      <div className="flex items-center gap-2.5">
                        <ShieldCheck className="size-4 text-primary shrink-0" />
                        <div>
                          <p className="font-semibold text-foreground text-xs">{item.name}</p>
                          <p className="text-muted-foreground text-[11px]">{item.detail}</p>
                        </div>
                      </div>
                      <span className="text-[11px] font-medium text-primary">Verified</span>
                    </div>)}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Use Cases Grid Section */}
        <div className="mt-16 pt-12 border-t border-border">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-2xl font-bold tracking-tight text-foreground font-default">
              Specialist access for every stage of refurbishment
            </h2>
            <p className="text-sm text-muted-foreground mt-2">
              From historic masonry work to complex structural refits, we adapt each scaffolding build to your site constraints and schedule.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {useCases.map((useCase, i) => {
            const IconComp = useCase.icon;
            return <motion.div key={i} data-index={i} initial={shouldReduceMotion ? false : {
              opacity: 0,
              y: 16
            }} whileInView={shouldReduceMotion ? false : {
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              amount: 0.2
            }} transition={{
              duration: 0.4,
              delay: i * 0.1
            }} className="h-full">
                  <Card className="h-full border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-200 flex flex-col justify-between">
                    <CardHeader className="pb-3">
                      <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                        <IconComp className="size-5" />
                      </div>
                      <CardTitle className="text-base font-bold text-foreground">
                        {useCase.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {useCase.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}